import React from 'react';

/**
 * Types and interfaces for the artifact system.
 *
 * Artifacts are outputs that can be displayed alongside a chat, such as documents,
 * code files, or other generated content. The artifact abstraction allows different
 * chat types to share the same side-by-side layout and rendering logic while
 * producing different types of outputs.
 */

/**
 * Enum representing the different types of artifacts that can be created.
 * Currently only DOCUMENT is supported, but this can be extended to support
 * other artifact types like code files, diagrams, etc.
 */
export enum ArtifactType {
  DOCUMENT = 'DOCUMENT',
  PLAN = 'PLAN',
  SOURCE_FILE = 'SOURCE_FILE',
  CANVAS = 'CANVAS',
}

/**
 * Props for artifact components.
 * @template T The type of data the artifact component renders
 */
export interface ArtifactComponentProps<T> {
  /** The artifact data to render */
  data: T;
  /** Whether the artifact is editable */
  editable: boolean;
  /** Optional callback when the close button is clicked (phone view) */
  onClose?: () => void;
  /**
   * Optional replacement for the artifact's own header. Layouts that already show a title for the
   * artifact pass this so the artifact contributes only its badges and actions to the layout's
   * header, rather than rendering a second title bar. When provided, the component must render its
   * result in place of its own header (in every state, including loading and error), since the
   * layout relies on it for its title and navigation controls.
   */
  renderHeader?: (slots: ArtifactHeaderSlots) => React.ReactNode;
}

/** The artifact-specific pieces of a header, for layouts that render the header themselves. */
export interface ArtifactHeaderSlots {
  /** Small status indicators shown next to the title (e.g. a version badge or source chip). */
  badges?: React.ReactNode;
  /** Artifact actions (e.g. save or refresh buttons). */
  actions?: React.ReactNode;
}

/**
 * An artifact instance managed by the useArtifacts hook.
 * @template T The type of data this artifact contains
 */
export interface Artifact<T> {
  /** The type of artifact (e.g., DOCUMENT) */
  artifactType: ArtifactType;
  /** Function to open/activate this artifact */
  open: () => void;
  /**
   * Optional callback to run before sending a message.
   * For documents, this ensures the document is saved before the message is sent.
   */
  beforeSendMessage?: () => Promise<void>;
  /** The artifact data */
  data: T;
  /** Component to render the artifact */
  Component: React.ComponentType<ArtifactComponentProps<T>>;
}

/**
 * Get the display label for an artifact type.
 */
export function getArtifactTypeLabel(artifactType: ArtifactType): string {
  switch (artifactType) {
    case ArtifactType.DOCUMENT:
      return 'Draft document';
    case ArtifactType.PLAN:
      return 'Plan';
    case ArtifactType.SOURCE_FILE:
      return 'Source file';
    case ArtifactType.CANVAS:
      return 'Canvas';
    default:
      return 'Artifact';
  }
}
